import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  NumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { FetchError } from '@sierralabs/react-material-ui';

import { ApiParams } from '../../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import {
  IdSearchableFetchOptions,
  useIdSearchableFetch
} from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { User } from '../../user/User.interface';
import { Task } from '../tasks/Task.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { HazardFilterOptions } from './components/HazardBoardFilter';
import { getHazardEntityName } from './Hazard.config';
import {
  Hazard,
  HazardAction,
  HazardActionVersion,
  HazardBoardColumnEntity,
  HazardBoardData,
  HazardCause,
  HazardCauseVersion,
  HazardControl,
  HazardControlVersion,
  HazardEntity,
  HazardExportEntity,
  ProductHazardBoardData,
  HazardReferenceTarget,
  HazardReferenceSource,
  HazardVersion
} from './Hazard.interface';

export function useUpdateHazard(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<Hazard>(
    `hazards/${id}`
  );
  return {
    updatedHazard: data,
    isUpdating: isLoading,
    updateError: error,
    updateHazard: patch
  };
}

export function useFindProductHazards(productId: number) {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<Hazard>>(
    `hazards/products/${productId}/hazards`
  );
  const findProductHazards = useIdSearchableFetch(get, WorkflowEntity.Hazard);
  return {
    productHazardList: data,
    isLoadingProductHazards: isLoading,
    loadingProductHazardsError: error,
    findProductHazards
  };
}

export function useFindHazard(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<Hazard>(`Hazards/${id}`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    hazard: data,
    isLoading,
    loadingError: error,
    findHazard: get
  };
}

export function useFindHazardVersion(id: number, version: number) {
  const { data, get, ...result } = useApiGet<HazardVersion>(
    `hazards/${id}/version/${version}`
  );
  return { ...result, hazardVersion: data, findHazardVersion: get };
}

export function useHazardEntityPrefixId(
  hazardEntity: HazardEntity | Partial<HazardEntity> | undefined,
  entity: HazardBoardColumnEntity
): string {
  return useMemo(() => {
    if (!hazardEntity?.id) {
      return '';
    }
    const { id } = hazardEntity;
    return getHazardEntityPrefix(entity) + id;
  }, [entity, hazardEntity]);
}

export function getHazardEntityPrefix(entity: HazardBoardColumnEntity): string {
  switch (entity) {
    case HazardBoardColumnEntity.Cause:
      return 'CAUSE-';
    case HazardBoardColumnEntity.Control:
      return 'CONTROL-';
    case HazardBoardColumnEntity.Action:
      return 'ACTION-';
    default:
      return '';
  }
}

// Hazard Cause hooks
export function useFindHazardCauses(hazardId: number) {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<HazardCause>>(
    `hazards/${hazardId}/causes`
  );
  const findHazardCauses = useIdSearchableFetch(get, WorkflowEntity.Cause);
  return {
    hazardCauseList: data,
    isLoadingHazardCauses: isLoading,
    loadingHazardCausesError: error,
    findHazardCauses
  };
}
export function useCreateHazardCause(hazardId: number) {
  const { data, isLoading, error, post } = useApiPost<HazardCause>(
    `hazards/${hazardId}/causes`
  );
  return {
    createdHazardCause: data,
    isCreatingHazardCause: isLoading,
    createHazardCauseError: error,
    createHazardCause: post
  };
}
export function useUpdateHazardCause(hazardId: number, hazardCauseId?: number) {
  const [path, setPath] = useState('');
  const [cause, setCause] = useState<Partial<HazardCause>>();
  const { data, isLoading, error, patch } = useApiPatch<HazardCause>(path);
  const updateHazardCause = useCallback(
    (cause: Partial<HazardCause>) => {
      setPath(`hazards/${hazardId}/causes/${hazardCauseId || cause.id}`);
      setCause(cause);
    },
    [hazardId, hazardCauseId]
  );
  useEffect(() => {
    if (path && cause) {
      patch(cause);
    }
  }, [patch, path, cause]);
  return {
    updatedHazardCause: data,
    isUpdatingHazardCause: isLoading,
    updateHazardCauseError: error,
    updateHazardCause
  };
}

export function useFindHazardCauseById(
  hazardId: number,
  hazardCauseId: number
) {
  const path = `hazards/${hazardId}/causes/${hazardCauseId}`;
  const { data, isLoading, error, get } = useApiGet<HazardCause>(path);

  return {
    hazardCause: data,
    isLoadingHazardCauses: isLoading,
    findHazardCauseError: error,
    findHazardCause: get
  };
}

export function useFindHazardCauseVersion(
  hazardId: number,
  hazardCauseId: number,
  version?: number
) {
  let path = `hazards/${hazardId}/causes/${hazardCauseId}/version/${version}`;
  const { data, isLoading, error, get } = useApiGet<HazardCauseVersion>(path);

  return {
    hazardCauseVersion: data,
    isLoadingHazardCauseVersion: isLoading,
    findHazardCauseVersionError: error,
    findHazardCauseVersion: get
  };
}

// Control Hooks
export function useFindHazardControls(hazardId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<HazardControl>
  >(`hazards/${hazardId}/controls`);
  const findHazardControls = useIdSearchableFetch(get, WorkflowEntity.Control);
  return {
    hazardControlList: data,
    isLoadingHazardControls: isLoading,
    loadingHazardControlsError: error,
    findHazardControls
  };
}

export function useCreateHazardControl(hazardId: number) {
  const { data, isLoading, error, post } = useApiPost<HazardControl>(
    `hazards/${hazardId}/controls`
  );
  return {
    createdHazardControl: data,
    isCreatingHazardControl: isLoading,
    createHazardControlError: error,
    createHazardControl: post
  };
}

export function useUpdateHazardControl(
  hazardId: number,
  hazardControlId?: number
) {
  const [path, setPath] = useState('');
  const [control, setControl] = useState<Partial<HazardControl>>();
  const { data, isLoading, error, patch } = useApiPatch<HazardControl>(path);
  const updateHazardControl = useCallback(
    (control: Partial<HazardControl>) => {
      setPath(`hazards/${hazardId}/controls/${hazardControlId || control.id}`);
      setControl(control);
    },
    [hazardId, hazardControlId]
  );
  useEffect(() => {
    if (path && control) {
      patch(control);
    }
  }, [patch, path, control]);
  return {
    updatedHazardControl: data,
    isUpdatingHazardControl: isLoading,
    updateHazardControlError: error,
    updateHazardControl
  };
}

export function useFindHazardControlById(
  hazardId: number,
  hazardControlId: number
) {
  const path = `hazards/${hazardId}/controls/${hazardControlId}`;
  const { data, isLoading, error, get } = useApiGet<HazardControl>(path);

  return {
    hazardControl: data,
    isLoadingHazardControl: isLoading,
    findHazardControlError: error,
    findHazardControl: get
  };
}

export function useFindHazardControlVersion(
  hazardId: number,
  hazardControlId: number,
  version?: number
) {
  let path = `hazards/${hazardId}/controls/${hazardControlId}/version/${version}`;
  const { data, isLoading, error, get } = useApiGet<HazardControlVersion>(path);

  return {
    hazardControlVersion: data,
    isLoadingHazardControlVersion: isLoading,
    findHazardControlVersionError: error,
    findHazardControlVersion: get
  };
}

// Hazard Action hooks
export function useFindHazardActions(hazardId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<HazardAction>
  >(`hazards/${hazardId}/actions`);
  const findHazardActions = useIdSearchableFetch(get, WorkflowEntity.Action);
  return {
    hazardActionList: data,
    isLoadingHazardActions: isLoading,
    loadingHazardActionsError: error,
    findHazardActions
  };
}

export function useCreateHazardAction(hazardId: number) {
  const { data, isLoading, error, post } = useApiPost<HazardAction>(
    `hazards/${hazardId}/actions`
  );
  return {
    createdHazardAction: data,
    isCreatingHazardAction: isLoading,
    createHazardActionError: error,
    createHazardAction: post
  };
}
export function useUpdateHazardAction(
  hazardId: number,
  hazardActionId?: number
) {
  const [path, setPath] = useState('');
  const [action, setAction] = useState<Partial<HazardAction>>();
  const { data, isLoading, error, patch } = useApiPatch<HazardAction>(path);
  const updateHazardAction = useCallback(
    (action: Partial<HazardAction>) => {
      setPath(`hazards/${hazardId}/actions/${hazardActionId || action.id}`);
      setAction(action);
    },
    [hazardId, hazardActionId]
  );
  useEffect(() => {
    if (path && action) {
      patch(action);
    }
  }, [patch, path, action]);
  return {
    updatedHazardAction: data,
    isUpdatingHazardAction: isLoading,
    updateHazardActionError: error,
    updateHazardAction
  };
}

export function useFindHazardActionById(
  hazardId: number,
  hazardActionId: number
) {
  const path = `hazards/${hazardId}/actions/${hazardActionId}`;
  const { data, isLoading, error, get } = useApiGet<HazardAction>(path);

  return {
    hazardAction: data,
    isLoadingHazardAction: isLoading,
    findHazardActionError: error,
    findHazardAction: get
  };
}

export function useFindHazardActionVersion(
  hazardId: number,
  hazardActionId: number,
  version?: number
) {
  let path = `hazards/${hazardId}/actions/${hazardActionId}/version/${version}`;
  const { data, isLoading, error, get } = useApiGet<HazardActionVersion>(path);

  return {
    hazardActionVersion: data,
    isLoadingHazardActionVersion: isLoading,
    findHazardActionVersionError: error,
    findHazardActionVersion: get
  };
}

export function useHazardTargetEntity(
  target: HazardReferenceTarget
): WorkflowEntity {
  return useMemo((): WorkflowEntity => {
    switch (target) {
      case 'actions':
        return WorkflowEntity.Action;
      case 'causes':
        return WorkflowEntity.Cause;
      case 'controls':
        return WorkflowEntity.Control;
      case 'user-needs':
        return WorkflowEntity.UserNeed;
      case 'design-inputs':
        return WorkflowEntity.DesignInput;
      case 'design-outputs':
        return WorkflowEntity.DesignOutput;
      case 'verifications':
        return WorkflowEntity.DesignVerification;
      case 'validations':
        return WorkflowEntity.DesignValidation;
    }
  }, [target]);
}

export function useFindHazardReferenceTargets(
  hazardId: number,
  target: HazardReferenceTarget
) {
  let path = `hazards/${hazardId}/${target}`;
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<{ id: number; title: string; task: Task }>>(path);
  const targetEntity: WorkflowEntity = useHazardTargetEntity(target);
  const findReferenceTargets = useIdSearchableFetch(get, targetEntity);
  return {
    targetEntityList: data,
    isLoadingTargets: isLoading,
    loadingTargetsError: error,
    findReferenceTargets
  };
}

export function useFindHazardReferences(
  hazardId: number,
  source: HazardReferenceSource,
  sourceId: number | undefined,
  target: HazardReferenceTarget
) {
  let path =
    source && sourceId
      ? `hazards/${hazardId}/${source}/${sourceId}/${target}`
      : `hazards/${hazardId}/${target}`;
  const { data, isLoading, error, get } =
    useApiGet<{ id: number; title: string; task: Task }[]>(path);
  return {
    references: data,
    isLoadingReferences: isLoading,
    loadingReferenceError: error,
    findReferences: get
  };
}

export function useAssignHazardReferences(
  hazardId: number,
  source: HazardReferenceSource,
  sourceId: number | undefined,
  target: HazardReferenceTarget
) {
  let path =
    source && sourceId
      ? `hazards/${hazardId}/${source}/${sourceId}/${target}`
      : `hazards/${hazardId}/${target}`;
  const { data, isLoading, error, post } =
    useApiPost<{ id: number; title: string; task: Task }>(path);
  const assignReferences = useCallback(
    (targetIds: number[]) => {
      post({ targetIds });
    },
    [post]
  );
  return {
    updatedSource: data,
    isAssigningReferences: isLoading,
    assigningReferenceError: error,
    assignReferences
  };
}

const hazardBoardOptions: IdSearchableFetchOptions[] = [
  WorkflowEntity.Action,
  WorkflowEntity.Cause,
  WorkflowEntity.Control
].map((entity: WorkflowEntity) => {
  return {
    entity,
    removeFrom: false,
    to: entity.concat('SearchId')
  } as IdSearchableFetchOptions;
});

// Hazard Board Hooks
export function useGetHazardBoard(hazardId: number) {
  const { data, isLoading, error, get } = useApiGet<HazardBoardData>(
    `hazards/${hazardId}/board`
  );
  const getHazardBoard = useIdSearchableFetch(get, hazardBoardOptions);
  return {
    hazardBoard: data,
    isLoading: isLoading,
    loadingError: error,
    getHazardBoard
  };
}

export function useGetProductHazardBoard(productId: number) {
  const { data, isLoading, error, get } = useApiGet<ProductHazardBoardData>(
    `hazards/products/${productId}/hazardBoard`
  );
  const getProductHazardBoard = useIdSearchableFetch(get, hazardBoardOptions);
  return {
    productHazardBoard: data,
    isLoading: isLoading,
    loadingError: error,
    getProductHazardBoard
  };
}

export function useGetHazardBoardReferences() {
  const [state, setState] = useState<{
    path: string;
    hazardId: number;
    referencedHazardIds: number[];
    apiParams: ApiParams;
  }>();
  const {
    data,
    isLoading,
    error,
    get: apiGet
  } = useApiGet<HazardBoardData[]>(state?.path || '');
  const get = useIdSearchableFetch(apiGet, hazardBoardOptions);
  useEffect(() => {
    if (state) {
      const { referencedHazardIds, apiParams } = state;
      get({
        referencedHazardIds,
        ...apiParams
      });
    }
  }, [get, state]);
  const getHazardBoardReferences = useCallback(
    (
      hazardId: number,
      referencedHazardIds: number[],
      apiParams: ApiParams = {}
    ) => {
      setState({
        path: `hazards/${hazardId}/board/references`,
        hazardId,
        referencedHazardIds,
        apiParams
      });
    },
    []
  );
  return {
    hazardBoardReferences: data,
    isLoadingReferences: isLoading,
    loadingReferencesError: error,
    getHazardBoardReferences
  };
}

export const getExportTitle = (entity: HazardExportEntity) => {
  switch (entity) {
    case HazardExportEntity.Cause:
      return getHazardEntityName('cause');
    case HazardExportEntity.Control:
      return getHazardEntityName('control');
    case HazardExportEntity.Action:
      return getHazardEntityName('action');
  }
};

export function useDownloadHazardDefaultTemplate(): [
  ApiResult<any>,
  (params: ApiParams & { entity: HazardExportEntity }) => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<any>('hazards/default-template');
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (params: ApiParams & { entity: HazardExportEntity }) => {
      // remove old document
      setBlob(undefined);
      get(params);
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}

export function useHazardFilterQueryParams() {
  const [status, setStatus] = useQueryParam(
    'status',
    withDefault<string[], string[]>(ArrayParam as any, [])
  );
  const [assigneeId, setAssigneeId] = useQueryParam(
    'assigneeId',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [authorId, setAuthorId] = useQueryParam(
    'authorId',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [assignedForReviewOnly, setAssignedForReviewOnly] = useQueryParam(
    'assignedForReviewOnly',
    withDefault(BooleanParam, false)
  );

  const options: HazardFilterOptions = useMemo(
    () => ({
      status: status || [],
      assignee: assigneeId || [],
      author: authorId || [],
      assignedForReviewOnly
    }),
    [status, assigneeId, authorId, assignedForReviewOnly]
  );

  const convertOptionsToParams = useCallback(
    (options: HazardFilterOptions): ApiParams => {
      const { assignee, status, author, assignedForReviewOnly } = options;
      const params: ApiParams = {
        status,
        assigneeId: assignee,
        authorId: author,
        assignedForReviewOnly
      };
      status?.length === 0 && delete params.status;
      assignee?.length === 0 && delete params.assigneeId;
      author?.length === 0 && delete params.authorId;
      typeof assignedForReviewOnly !== 'boolean' &&
        delete params.assignedForReviewOnly;
      return params;
    },
    []
  );

  const getParams = useCallback((): ApiParams => {
    return convertOptionsToParams(options);
  }, [options]);

  return {
    options,
    setStatus,
    setAssigneeId,
    setAssignedForReviewOnly,
    setAuthorId,
    getParams,
    convertOptionsToParams
  };
}
